const API_BASE = import.meta.env.VITE_API_BASE ?? "";
const GET_CACHE_TTL_MS = 2_000;
const getCache = new Map<string, { expiresAt: number; promise: Promise<unknown> }>();

async function get<T>(path: string): Promise<T> {
  const cacheKey = `${API_BASE}${path}`;
  const now = Date.now();
  const cached = getCache.get(cacheKey);
  if (cached && cached.expiresAt > now) return cached.promise as Promise<T>;

  const promise = fetch(`${API_BASE}${path}`).then(async (res) => {
    if (!res.ok) throw new Error(`${path} -> ${res.status}`);
    return (await res.json()) as T;
  });

  getCache.set(cacheKey, { expiresAt: now + GET_CACHE_TTL_MS, promise });
  promise.catch(() => {
    const current = getCache.get(cacheKey);
    if (current?.promise === promise) getCache.delete(cacheKey);
  });
  return promise;
}

export interface CurrentResponse {
  ok: true;
  fetched_at: number;
  lifetime: {
    matches_played: number;
    seconds_played: number;
    kills: number;
    deaths: number;
    wins: number;
    losses: number;
    score: number;
    damage: number;
    headshots: number;
  };
  derived: {
    kd: number;
    accuracy: number;
    headshot_rate: number;
    win_rate: number;
    hours_played: number;
    avg_kills_per_match: number;
  };
  breakdowns: {
    gamemodes: LifetimeGamemode[];
    maps: LifetimeMap[];
    weapons: LifetimeWeapon[];
  };
  bests: ProfileBestsBlock | null;
}

export interface ProfileBestsBlock {
  fetched_at: number;
  data: ProfileBestsData;
}

export interface ProfileBestsData {
  most_kills_round: number | null;
  most_kills_life: number | null;
  most_damage_round: number | null;
  most_damage_life: number | null;
  most_score_round: number | null;
  most_score_life: number | null;
  most_assists_round: number | null;
  most_revives_round: number | null;
  most_headshots_life: number | null;
  most_vehicles_destroyed_round: number | null;
  most_objectives_captured_round: number | null;
  most_squad_wipes_round: number | null;
  longest_killstreak: number | null;
  longest_kill_meters: number | null;
  longest_marksman_kill_meters: number | null;
  biggest_multikill: number | null;
}

export interface LifetimeGamemode {
  id: string | null;
  name: string;
  image: string | null;
  alt_image: string | null;
  matches: number;
  kills: number;
  deaths: number;
  assists: number;
  score: number;
  wins: number;
  losses: number;
  headshots: number;
  seconds_played: number;
  kd: number;
  win_rate: number | null;
  kills_per_match: number | null;
  hours_played: number;
}

export interface LifetimeMap {
  id: string | null;
  name: string;
  image: string | null;
  matches: number;
  wins: number;
  losses: number;
  seconds_played: number;
  win_rate: number | null;
  hours_played: number;
}

export interface LifetimeWeapon {
  id: string;
  name: string;
  type: string | null;
  image: string | null;
  alt_image: string | null;
  kills: number;
  damage: number;
  assists_damage: number;
  body_kills: number;
  headshots: number;
  hipfire_kills: number;
  multi_kills: number;
  scoped_kills: number;
  shots_fired: number;
  shots_hit: number;
  spawns: number;
  seconds_equipped: number;
  accuracy: number | null;
  headshot_rate: number | null;
  kills_per_minute: number | null;
  damage_per_minute: number | null;
  damage_per_kill: number | null;
  hits_per_kill: number | null;
  hours_equipped: number;
}

export interface MatchRow {
  id: number;
  window_start: number;
  window_end: number;
  merged_match_count: number;
  derivation_kind?: "single" | "merged" | "fallback";
  confidence_flags?: MatchConfidenceFlag[];
  kills_delta: number;
  deaths_delta: number;
  assists_delta?: number;
  score_delta: number;
  damage_delta: number;
  seconds_played_delta: number;
  wins_delta: number;
  losses_delta: number;
  headshots_delta: number;
  gamemode_hint: string | null;
  map_hint?: string | null;
  map_image?: string | null;
  map_id?: string | null;
  weapons?: MatchWeapon[];
}

export type MatchConfidenceFlag =
  | "merged_window"
  | "fallback_gameplay_delta"
  | "weapon_data_missing"
  | "gamemode_inferred"
  | "gamemode_missing"
  | "negative_delta_skipped";

export interface MatchWeapon {
  id: string;
  name: string;
  type: string | null;
  image: string | null;
  alt_image: string | null;
  kills_delta: number;
  damage_delta: number;
  headshots_delta: number;
  shots_fired_delta: number;
  shots_hit_delta: number;
  seconds_equipped_delta: number;
}

export interface TimeseriesResponse {
  ok: true;
  bucket: "match" | "day";
  metric: string;
  // v is null for windows where the metric is undefined (e.g. win_rate with no decided
  // match) so charts can render a gap rather than a misleading zero.
  points: Array<{ t: number | string; v: number | null; merged?: number }>;
}

export interface ActivityDay {
  date: string;
  minutes: number;
  matches: number;
  kills: number;
  wins: number;
  losses: number;
  score: number;
  damage: number;
  deaths: number;
}

export interface ActivityResponse {
  ok: true;
  from: string;
  to: string;
  metric: "minutes" | "matches" | "kills" | "wins" | "score";
  days: ActivityDay[];
}

export interface HealthResponse {
  ok: true;
  now: number;
  schema_version: number;
  latest_poll_at: number | null;
  latest_snapshot_at: number | null;
  latest_match_at: number | null;
  snapshot_count: number;
  match_count: number;
  daily_rollup_count: number;
  latest_snapshot_has_gamemodes: boolean;
  latest_snapshot_has_weapons: boolean;
  latest_snapshot_has_maps: boolean;
  stale_snapshot: boolean;
  stale_poll: boolean;
}

export type PollHistoryRange = "1h" | "24h" | "7d" | "30d";

export interface PollHistoryBucket {
  ts: number;
  total: number;
  by_status: Record<string, number>;
}

export interface PollHistoryResponse {
  ok: true;
  range: PollHistoryRange;
  now: number;
  window_start: number;
  window_end: number;
  bucket_seconds: number;
  totals: {
    polls: number;
    successes: number;
    failures: number;
    success_rate: number | null;
    by_status: Record<string, number>;
  };
  last_success_at: number | null;
  last_success_status: string | null;
  last_failure_at: number | null;
  last_failure_status: string | null;
  last_failure_error: string | null;
  last_failure_gametools_status: number | null;
  buckets: PollHistoryBucket[];
}

export function invalidateApiCache() {
  getCache.clear();
}

export const api = {
  activity: (from?: string, to?: string, metric: ActivityResponse["metric"] = "minutes", timeZone = browserTimeZone()) => {
    const q = new URLSearchParams({ metric });
    if (from) q.set("from", from);
    if (to) q.set("to", to);
    if (timeZone) q.set("timeZone", timeZone);
    return get<ActivityResponse>(`/api/activity?${q}`);
  },
  current: () => get<CurrentResponse>("/api/current"),
  health: () => get<HealthResponse>("/api/health"),
  pollHistory: (range: PollHistoryRange = "24h") => get<PollHistoryResponse>(`/api/poll-history?range=${range}`),
  matches: (from?: number, to?: number, limit?: number) => {
    const q = new URLSearchParams();
    if (from) q.set("from", String(from));
    if (to) q.set("to", String(to));
    if (limit) q.set("limit", String(limit));
    return get<{ ok: true; matches: MatchRow[] }>(`/api/matches?${q}`);
  },
  timeseries: (metric: string, bucket: "match" | "day", from?: number, to?: number, timeZone = browserTimeZone()) => {
    const q = new URLSearchParams({ metric, bucket });
    if (from) q.set("from", String(from));
    if (to) q.set("to", String(to));
    // Day buckets are grouped server-side in this timezone so they match the activity calendar.
    if (timeZone) q.set("timeZone", timeZone);
    return get<TimeseriesResponse>(`/api/timeseries?${q}`);
  },
};

function browserTimeZone(): string | undefined {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
}
